import { getTenantPathPrefix, isOrgPickerPath } from './pathTenant'

const STORAGE_KEY = 'continuum_go_post_auth_return'
const STORAGE_VERSION = 1 as const

export type StoredPostAuthReturn =
    | { v: typeof STORAGE_VERSION; kind: 'bare'; path: string }
    | { v: typeof STORAGE_VERSION; kind: 'tenanted'; path: string }

const MAX_PATH_LEN = 4096

function normalizePathForCompare(p: string): string {
    const x = p.split('?')[0].split('#')[0]
    if (x.length > 1 && x.endsWith('/')) return x.slice(0, -1)
    return x
}

/** Reject open redirects and protocol tricks; allow same-app relative paths only. */
export function isSafeAppRelativePath(pathWithQueryHash: string): boolean {
    if (!pathWithQueryHash.startsWith('/') || pathWithQueryHash.length > MAX_PATH_LEN) return false
    if (pathWithQueryHash.startsWith('//')) return false
    return !pathWithQueryHash.includes('\0')
}

export function classifyUrlPath(pathnameWithQueryHash: string): 'bare' | 'tenanted' | 'skip' {
    const pathOnly = pathnameWithQueryHash.split('#')[0]
    const pathname = pathOnly.split('?')[0]
    if (!isOrgPickerPath(pathname) && pathname.split('/').filter(Boolean)[0] && /\.[a-zA-Z0-9]+$/.test(pathname.split('/').filter(Boolean)[0]!))
        return 'skip'
    if (isOrgPickerPath(pathname)) return 'skip'
    if (getTenantPathPrefix(pathname)) return 'tenanted'
    return 'bare'
}

function writeStored(raw: StoredPostAuthReturn): void {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(raw))
}

function readStored(): StoredPostAuthReturn | null {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    try {
        const o = JSON.parse(raw) as StoredPostAuthReturn
        if (o?.v !== STORAGE_VERSION || (o.kind !== 'bare' && o.kind !== 'tenanted')) return null
        if (!isSafeAppRelativePath(o.path)) return null
        return o
    } catch {
        return null
    }
}

function clearStored(): void {
    sessionStorage.removeItem(STORAGE_KEY)
}

/** Remember where to return after auth / tenant resolution (session tab). */
export function savePostAuthReturnFromWindow(): void {
    const path = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (!isSafeAppRelativePath(path)) return
    const kind = classifyUrlPath(path)
    if (kind === 'skip') return
    const payload: StoredPostAuthReturn = { v: STORAGE_VERSION, kind, path }
    writeStored(payload)
    console.info('[Continuum] Saved post-auth return:', path)
}

export function clearPostAuthReturn(): void {
    clearStored()
}

/** Legacy bookmark `/login/{three-character customer id}` (same rule as PageGetTenant). */
const LEGACY_LOGIN_PATH = /^\/login\/([A-Za-z0-9]{3})\/?$/i

export function legacyLoginCodeFromPath(pathWithQueryHash: string): string | null {
    const pathOnly = pathWithQueryHash.split('?')[0].split('#')[0]
    const pathname = normalizePathForCompare(pathOnly)
    const m = pathname.match(LEGACY_LOGIN_PATH)
    return m ? m[1].toUpperCase() : null
}

/** Saved bare deep link from `BareLoginRedirect` after `/login/abc` → `/switch`. */
export function legacyLoginCodeFromSavedReturn(): string | null {
    const s = readStored()
    if (!s || s.kind !== 'bare') return null
    return legacyLoginCodeFromPath(s.path)
}

/**
 * On org-picker URL after MSAL has a session: go straight back to a saved `/_tenant/...` URL.
 * Bare paths (no tenant segment) stay for organisation selection.
 */
export function consumeTenantedReturnAfterMsal(): string | null {
    const s = readStored()
    if (!s || s.kind !== 'tenanted') return null
    clearStored()
    console.info('[Continuum] Restoring after MSAL (tenanted):', s.path)
    return s.path
}

function rewriteLeadingTenantSegment(fullPathWithQueryHash: string, newTenantSegment: string): string {
    const hashIdx = fullPathWithQueryHash.indexOf('#')
    const beforeHash = hashIdx >= 0 ? fullPathWithQueryHash.slice(0, hashIdx) : fullPathWithQueryHash
    const hash = hashIdx >= 0 ? fullPathWithQueryHash.slice(hashIdx) : ''
    const qIdx = beforeHash.indexOf('?')
    const pathname = qIdx >= 0 ? beforeHash.slice(0, qIdx) : beforeHash
    const search = qIdx >= 0 ? beforeHash.slice(qIdx) : ''
    const parts = pathname.split('/').filter(Boolean)
    const seg = newTenantSegment.startsWith('_') ? newTenantSegment : `_${newTenantSegment}`
    if (!parts.length) {
        return `/${seg}/` + search + hash
    }
    parts[0] = seg
    return '/' + parts.join('/') + search + hash
}

/**
 * After user picks an organisation: map stored bare path to `/_segment/...` or rewrite tenant prefix.
 * `segment` is `_${encodeURIComponent(displayId)}` (same as PageGetTenant).
 */
export function consumeReturnAfterTenantSelected(segment: string): string | null {
    const s = readStored()
    if (!s) return null
    clearStored()

    if (s.kind === 'bare') {
        const p = s.path.startsWith('/') ? s.path : `/${s.path}`
        const seg = segment.startsWith('_') ? segment : `_${segment}`
        const target = `/${seg}${p}`
        console.info('[Continuum] Restoring after tenant select (bare → tenanted):', target)
        return target
    }

    const out = rewriteLeadingTenantSegment(s.path, segment)
    console.info('[Continuum] Restoring after tenant select (tenanted rewrite):', out)
    return out
}

/**
 * After full reload on tenant home (e.g. navigateToLogin), jump back to a saved deep link once JWT exists.
 */
export function consumeTenantedReturnIfDeepLinkPending(tenantPrefix: string, hasToken: boolean): string | null {
    if (!hasToken || !tenantPrefix) return null
    const normPrefix = tenantPrefix.replace(/\/$/, '')
    const s = readStored()
    if (!s || s.kind !== 'tenanted') return null

    const currentFull = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (currentFull === s.path) {
        clearStored()
        return null
    }

    const targetPathOnly = s.path.split('?')[0].split('#')[0]
    if (!targetPathOnly.startsWith(normPrefix + '/') && normalizePathForCompare(targetPathOnly) !== normalizePathForCompare(normPrefix)) {
        if (!targetPathOnly.startsWith(normPrefix)) {
            clearStored()
            return null
        }
    }

    const curPathOnly = window.location.pathname
    const atTenantRoot = normalizePathForCompare(curPathOnly) === normalizePathForCompare(normPrefix)

    if (!atTenantRoot) return null

    clearStored()
    console.info('[Continuum] Restoring saved deep link after login:', s.path)
    return s.path
}
